import { ChevronDown } from "@untitledui/icons";
import type { FC, HTMLAttributes, MouseEventHandler, ReactNode } from "react";
import { Badge } from "@/components/base/badges/badges";
import { cx, sortCx } from "@/utils/cx";
import { CustomLink } from "./CustomLink";

const styles = sortCx({
	root: "group relative flex w-full cursor-pointer items-center rounded-md outline-focus-ring transition duration-100 ease-linear select-none focus-visible:z-10",
	rootHover: "hover:bg-primary_hover",
	rootBrandFocus:
		"focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#ffffff,0_0_0_4px_#213a53]",
	rootLightFocus: "focus-visible:outline-2 focus-visible:outline-offset-2",
});

interface NavItemBaseProps {
	/** Whether the nav item shows only an icon. */
	iconOnly?: boolean;
	/** Custom background class for selected state */
	selectedClassName?: string;
	/** Custom class for the icon */
	iconClassName?: string;
	/** Custom class for the text label */
	textClassName?: string;
	/** Custom class to apply to the CustomLink wrapper */
	linkClassName?: string;
	/** Override the default hover background class (e.g. for on-brand dark surfaces) */
	hoverClassName?: string;
	/** Whether the collapsible nav item is open. */
	open?: boolean;
	/** URL to navigate to when the nav item is clicked. Optional for parent-only folders. */
	href?: string;
	/** Type of the nav item. */
	type: "link" | "parent" | "child";
	/** Icon component to display. */
	icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
	/** Badge to display. */
	badge?: ReactNode;
	/** Leading status dot (Figma NavItem Dot property) */
	showStatusDot?: boolean;
	/** Trailing chevron for parent items. Defaults to true when type is parent. */
	showChevron?: boolean;
	/** Brand (dark sidebar) visual treatment */
	variant?: "light" | "brand";
	/** Disabled appearance */
	isDisabled?: boolean;
	/** Whether to truncate the label text. */
	truncate?: boolean;
	/** Handler for click events. */
	onClick?: MouseEventHandler;
	/** Optional data-testid for nav automation hooks. */
	testId?: string;
	/** Content to display. */
	children?: ReactNode;
}

/**
 * Primary sidebar navigation row (Figma NavItem / NavItemDropdown trigger).
 */
export const NavItemBase = ({
	type,
	badge,
	href,
	icon: Icon,
	children,
	truncate = true,
	onClick,
	open = false,
	selectedClassName = "bg-active",
	iconClassName = "text-fg-quaternary",
	textClassName,
	linkClassName,
	hoverClassName,
	showStatusDot = false,
	showChevron,
	variant = "light",
	isDisabled = false,
	testId,
}: NavItemBaseProps) => {
	const isBrand = variant === "brand";
	const shouldShowChevron = showChevron ?? type === "parent";
	const rootHover = hoverClassName ?? styles.rootHover;
	const focusClass = isBrand ? styles.rootBrandFocus : styles.rootLightFocus;

	const statusDot = showStatusDot ? (
		<span
			aria-hidden
			className={cx(
				"mr-2 size-2 shrink-0 rounded-full bg-success-500",
				isDisabled && "opacity-40",
			)}
		/>
	) : null;

	const iconElement = Icon && (
		<Icon
			aria-hidden="true"
			className={cx(
				"size-5 shrink-0 transition-inherit-all",
				isBrand ? "mr-0" : "mr-2",
				iconClassName,
			)}
		/>
	);

	const badgeElement =
		badge && (typeof badge === "string" || typeof badge === "number") ? (
			<Badge className="ml-auto" color="gray" type="pill-color" size="sm">
				{badge}
			</Badge>
		) : (
			badge
		);

	const labelClass = (isActive: boolean) =>
		cx(
			"flex-1 text-md font-bold transition-inherit-all",
			truncate && "truncate",
			isBrand
				? isActive
					? "text-[rgba(255,255,255,0.9)]"
					: cx("text-[rgba(255,255,255,0.6)]", textClassName)
				: cx(
						"text-secondary group-hover:text-secondary_hover",
						isActive && "text-secondary_hover",
						textClassName,
					),
		);

	const contentRow = (
		isActive: boolean,
		options?: { withChevron?: boolean },
	) => (
		<>
			{statusDot}
			<span
				className={cx(
					"flex min-w-0 flex-1 items-center",
					isBrand ? "gap-2" : "gap-0",
				)}
			>
				{iconElement}
				<span className={labelClass(isActive)}>{children}</span>
			</span>
			{badgeElement}
			{options?.withChevron && shouldShowChevron && (
				<ChevronDown
					aria-hidden="true"
					className={cx(
						"ml-3 size-4 shrink-0 stroke-[2.5px] will-change-transform",
						"transition-transform duration-300 ease-out",
						isBrand ? "text-[rgba(255,255,255,0.6)]" : "text-fg-quaternary",
						open ? "rotate-180" : "rotate-0",
					)}
				/>
			)}
		</>
	);

	const rootClass = cx(
		"px-3 py-2",
		styles.root,
		rootHover,
		focusClass,
		isDisabled && "pointer-events-none opacity-40",
	);

	if (type === "parent") {
		// Folder-only parents (no href) toggle in-place — do not navigate.
		if (!href) {
			return (
				<button
					type="button"
					className={cx(
						rootClass,
						"flex items-center text-left",
						linkClassName,
					)}
					data-testid={testId}
					aria-expanded={open}
					aria-disabled={isDisabled || undefined}
					disabled={isDisabled}
					onClick={onClick}
				>
					{contentRow(Boolean(linkClassName && selectedClassName), {
						withChevron: true,
					})}
				</button>
			);
		}

		return (
			<CustomLink
				to={href}
				activeOptions={{
					exact: false,
					includeSearch: false,
					includeHash: false,
				}}
				className={cx(rootClass, "flex items-center")}
				data-testid={testId}
				inactiveProps={{
					className: linkClassName,
				}}
				activeProps={{
					className: cx(selectedClassName, textClassName),
				}}
				aria-expanded={open || undefined}
				aria-disabled={isDisabled || undefined}
				onClick={onClick}
			>
				{({ isActive }) => contentRow(isActive, { withChevron: true })}
			</CustomLink>
		);
	}

	if (type === "child" && href) {
		return (
			<CustomLink
				to={href}
				activeOptions={{
					exact: false,
					includeSearch: false,
					includeHash: false,
				}}
				className={cx(
					"py-2 pr-3 pl-10 text-left",
					styles.root,
					rootHover,
					focusClass,
				)}
				data-testid={testId}
				inactiveProps={{
					className: linkClassName,
				}}
				activeProps={{
					className: cx(
						selectedClassName,
						!isBrand && "hover:bg-secondary_hover",
						textClassName,
					),
					"aria-current": "page",
				}}
				aria-disabled={isDisabled || undefined}
				onClick={onClick}
			>
				{({ isActive }) => (
					<>
						<span
							className={cx(
								"flex-1 text-md font-bold transition-inherit-all",
								truncate && "truncate",
								isBrand
									? isActive
										? "text-[rgba(255,255,255,0.9)]"
										: cx("text-[rgba(255,255,255,0.6)]", textClassName)
									: !isActive
										? cx(
												"text-[#535862] font-body text-base font-bold leading-6",
												textClassName,
											)
										: "text-secondary_hover",
							)}
						>
							{children}
						</span>
						{badgeElement}
					</>
				)}
			</CustomLink>
		);
	}

	if (href) {
		return (
			<CustomLink
				to={href}
				activeOptions={{
					exact: false,
					includeSearch: false,
					includeHash: false,
				}}
				className={cx(rootClass, "text-left")}
				data-testid={testId}
				inactiveProps={{
					className: linkClassName,
				}}
				activeProps={{
					className: cx(
						selectedClassName,
						!isBrand && "hover:bg-secondary_hover",
					),
					"aria-current": "page",
				}}
				aria-disabled={isDisabled || undefined}
				onClick={onClick}
			>
				{({ isActive }) => contentRow(isActive)}
			</CustomLink>
		);
	}

	return null;
};
